import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import Link from 'next/link';
import { useRouter } from 'next/router';
import Typography from '../../atoms/typography';
import { StyledContent } from '../../theme/page';
import FastlyPicture from '../../atoms/fastly-picture';
import Button from '../../atoms/button';

const FASTLY_ON = process.env.NEXT_PUBLIC_FASTLY_ON === 'true';

const SectionWrapper = styled.section`
  background-color: ${(props) => props.theme.color.brandNeutral};
  position: relative;
  z-index: ${({ theme }) => theme.layers.backstage};

  @media (min-width: ${(props) => props.theme.breakpoint.small}) {
    align-items: center;
    display: flex;
    min-height: ${(props) => (props.hasImage ? 'calc(35rem + 6rem)' : '150px')};
  }

  ::before,
  ::after {
    background-image: url(${process.env.NEXT_PUBLIC_ASSET_HOST}/images/maisonette-header-bg.jpg);
    background-repeat: no-repeat;
    content: "";
    height: 100%;
    position: absolute;
    top: 0;
    width: 550px;
    z-index: -1;

    @media (min-width: ${(props) => props.theme.breakpoint.small}) {
      max-width: 50%;
    }
  }

  ::before {
    background-position: 0 0;
    display: none;
    left: 0;

    @media (min-width: ${(props) => props.theme.breakpoint.small}) {
      display: block;
    }
  }

  ::after {
    background-position: right 0;
    right: 0;
  }

  ${StyledContent} {
    width: 100%;

    @media (min-width: ${(props) => props.theme.breakpoint.small}) {
      align-items: center;
      display: flex;
      justify-content: center;
    }
  }
`;

const BrandContent = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  text-align: center;

  @media (min-width: ${(props) => props.theme.breakpoint.small}) {
    flex: 0 0 auto;
    width: auto;
  }
`;

const Heading = styled(Typography)`
  color: ${(props) => props.theme.color.brand};
  line-height: 1.2;
  font-size: ${({ theme }) => theme.modularScale.thirtyTwo};
`;

const SubHeading = styled(Typography)`
  color: ${(props) => props.theme.color.brand};
  letter-spacing: 0.24em;
  font-size: ${({ theme }) => theme.modularScale.fourteen};
  margin-bottom: ${({ theme }) => theme.modularScale.twentyFour};
  text-transform: uppercase;

   > span {
    color: ${({ theme }) => theme.color.brandLight};
   }

  @media (min-width: ${({ theme }) => theme.breakpoint.small}) {
    margin-bottom: ${({ theme }) => theme.modularScale.sixteen};
  }
`;

const Paragraph = styled(Typography)`
  color: ${(props) => props.theme.color.brand};
  line-height: 1.5;
  font-size: ${({ theme }) => theme.modularScale.sixteen};
  margin-top: ${({ theme }) => theme.modularScale.twentyFour};
  max-width: 425px;
`;

const BrandFigure = styled.figure`
  justify-content: center;
  width: 192px;

  @media (min-width: ${(props) => props.theme.breakpoint.small}) {
    flex: 0 0 auto;
    margin-left: auto;
    width: 224px;
  }

  @media (min-width: ${(props) => props.theme.breakpoint.medium}) {
    margin-left: 60px;
    width: 304px;
  }

  ${(props) => (props.mobile ? `
    margin-top: ${props.theme.modularScale.sixteen};

    @media (min-width: ${props.theme.breakpoint.small}) {
      display: none;
    }
  ` : `
    display: none;

    @media (min-width: ${props.theme.breakpoint.small}) {
      display: block;
    }
  `)}
`;

const BrandImage = styled.img`
  display: block;
  border-radius: 9999px;
  max-width: 100%;
  width: 224px;
  height: auto;

  @media (min-width: ${(props) => props.theme.breakpoint.medium}) {
    width: 304px;
  }
`;

const FilterWrapper = styled.div`
  display: grid;
  grid-gap: 1.6rem;
  grid-template-columns: repeat(2, minmax(0, 12rem));
  margin-top: ${(props) => props.theme.modularScale.xlarge};

  @media (min-width: ${(props) => props.theme.breakpoint.small}) {
    grid-template-columns: repeat(2, minmax(0, 13rem));
    margin-top: ${(props) => props.theme.modularScale['2xlarge']};
  }

  @media (min-width: ${(props) => props.theme.breakpoint.large}) {
    grid-template-columns: repeat(4, minmax(0, 13rem));
  }
`;

const FilterLink = styled(Button)`
  display: flex;
  flex-basis: 100%;
  justify-content: center;
  line-height: 2.8rem; /* 32 - 4 (borders) */
  padding: 0;
`;

const POCOwnedWrapper = styled('span', { shouldForwardProp: (prop) => prop !== 'multiplepocvalues' })`
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (min-width: ${({ theme }) => theme.breakpoint.small}) {
    flex-direction: ${({ multiplepocvalues }) => (multiplepocvalues ? 'column' : 'row')};
  }
`;

const POCOwnedCopy = styled(Typography)`
  color: ${({ theme }) => theme.color.brand};
  font-size: ${({ theme }) => theme.modularScale.fourteen};
`;

const Separator = styled.span`
  display: none;

  @media (min-width: ${({ theme }) => theme.breakpoint.small}) {
    display: inline;
    margin: 0 ${({ theme }) => theme.modularScale.eight};
    color: ${({ theme }) => theme.color.peach};
  }
`;

const Plus = styled.span`
  color: ${({ theme }) => theme.color.peach};
`;

const Filters = (props) => {
  const router = useRouter();

  const getHref = (filters) => {
    const { query: { brand } } = router;
    let pathname = `/brands/${brand}`;

    const { cats = null, refinements = null } = filters;
    pathname = cats ? pathname += `/${cats.join('/')}` : pathname;
    pathname = refinements ? pathname += `?${refinements.join('&')}` : pathname;

    return pathname;
  };

  const isFacetActive = (filters) => {
    const url = new URL(router.asPath, process.env.NEXT_PUBLIC_CLIENT_HOST);
    const facetUrl = new URL(getHref(filters), process.env.NEXT_PUBLIC_CLIENT_HOST);
    [...url.searchParams.keys()].forEach((key) => {
      if (key !== 'variants.age_range') url.searchParams.delete(key);
    });
    return decodeURIComponent(facetUrl.href) !== decodeURIComponent(url.href);
  };

  if (!props.filters || props.filters.length === 0) return null;

  return (
    <FilterWrapper>
      {
        props.filters.map(([name, filters]) => (
          <Link key={name} href={getHref(filters)} passHref>
            <FilterLink
              outline={isFacetActive(filters)}
              text={name}
              isLink
            />
          </Link>
        ))
      }
    </FilterWrapper>
  );
};

Filters.defaultProps = {
  filters: null
};

Filters.propTypes = {
  filters: PropTypes.array
};

const SubHeadingWithMeta = (props) => {
  if (!props.subHeading) return '';

  const renderBrandSubHeadingWithMeta = () => {
    const multiplePOCValues = props.meta_data?.poc_owned?.length > 1;

    const renderPOCValues = () => props.meta_data?.poc_owned?.map((value, idx) => (
      <React.Fragment key={`${value}-${idx}`}>
        {
          (!!multiplePOCValues && idx > 0)
            ? (
              <Plus>
                {' '}
                +
                {' '}
              </Plus>
            )
            : null
        }
        <POCOwnedCopy element="span" like="label-1">
          {value}
        </POCOwnedCopy>
      </React.Fragment>
    ));

    return (
      <SubHeading element="h2" data-test-id='plp_subheading' like="label-1">
        <POCOwnedWrapper multiplepocvalues={multiplePOCValues}>
          <span>
            {props.subHeading}
            {' '}
          </span>
          {
            props.meta_data?.poc_owned?.length > 0 && !multiplePOCValues && (
              <Separator>
                {' '}
                |
                {' '}
              </Separator>
            )
          }
          {renderPOCValues()}
        </POCOwnedWrapper>
      </SubHeading>
    );
  };

  return props.meta_data && props.isBrand
    ? renderBrandSubHeadingWithMeta()
    : <SubHeading element="h2" like="label-1" data-test-id='plp_header'>{props.subHeading}</SubHeading>;
};

SubHeadingWithMeta.defaultProps = {
  meta_data: null,
  subHeading: '',
  isBrand: false
};

SubHeadingWithMeta.propTypes = {
  meta_data: PropTypes.object,
  subHeading: PropTypes.node,
  isBrand: PropTypes.bool
};

const PageHeader = (props) => (
  <SectionWrapper hasImage={props.image}>
    <StyledContent>
      <BrandContent>
        <header>
          {props.children}

          <SubHeadingWithMeta
            subHeading={props.subHeading}
            meta_data={props.meta_data}
            isBrand={props.isBrand}
          />

          {
            props.heading
              ? <Heading element="h1" like="heading-3">{props.heading}</Heading>
              : ''
          }
        </header>

        {
          props.image && (
            <BrandFigure mobile aria-hidden="true">
              {
                FASTLY_ON
                  ? (
                    <FastlyPicture
                      type="brand-header"
                      Image={BrandImage}
                      src={props.image}
                      alt={props.heading}
                    />
                  )
                  : <BrandImage src={props.image} alt={props.heading} />
              }
            </BrandFigure>
          )
        }

        {
          props.text
            ? <Paragraph element="p" like="paragraph-2">{props.text}</Paragraph>
            : ''
        }

        <Filters filters={props.brandFilters} />
      </BrandContent>

      {
        props.image && (
          <BrandFigure>
            {
              FASTLY_ON
                ? (
                  <FastlyPicture
                    type="brand-header"
                    Image={BrandImage}
                    src={props.image}
                    alt={props.heading}
                  />
                )
                : <BrandImage src={props.image} alt={props.heading} />
            }
          </BrandFigure>
        )
      }
    </StyledContent>
  </SectionWrapper>
);

PageHeader.defaultProps = {
  brandFilters: null,
  children: null,
  heading: '',
  image: '',
  subHeading: '',
  text: '',
  meta_data: null,
  isBrand: false
};

PageHeader.propTypes = {
  brandFilters: PropTypes.array,
  children: PropTypes.node,
  heading: PropTypes.node,
  image: PropTypes.string,
  text: PropTypes.string,
  subHeading: PropTypes.node,
  meta_data: PropTypes.object,
  isBrand: PropTypes.bool
};

export default PageHeader;
